import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Key,
  Fingerprint,
  FileCheck,
  CheckCircle2,
  ArrowRight,
  Building2,
  CreditCard,
  Smartphone,
  BadgeCheck,
  ChevronDown
} from 'lucide-react';
import { Link } from 'react-router-dom';
import BookDemoModal from '../components/BookDemoModal';
import ContactSalesModal from '../components/ContactSalesModal';
import ParticleNetwork from '../components/ParticleNetwork';
import FloatingShapes from '../components/FloatingShapes';

// Philippine-specific features
const coreFeatures = [
  {
    icon: FileCheck,
    title: 'Advanced OCR Extraction',
    titleFil: 'Advanced OCR Extraction',
    description: 'Automated data extraction from 11+ Philippine IDs with 99.9% accuracy - no more manual typing.',
    descriptionFil: 'Awtomatikong pagkuha ng datos mula sa 11+ na Philippine IDs na may 99.9% accuracy.'
  },
  {
    icon: 'logo',
    title: 'QR & Barcode Validation',
    titleFil: 'QR & Barcode Validation',
    description: 'Multi-format barcode scanning for PhilID (QR), Driver\'s License (PDF417), and Postal ID - protected against tampered physical IDs.',
    descriptionFil: 'Multi-format barcode scanning para sa PhilID (QR), Driver\'s License (PDF417), at Postal ID - protektado laban sa tampered na physical IDs.'
  },
  {
    icon: Fingerprint,
    title: 'Biometric Verification',
    titleFil: 'Biometric Verification',
    description: 'Face matching with liveness detection and anti-spoofing to prevent identity theft.',
    descriptionFil: 'Face matching na may liveness detection at anti-spoofing.'
  },
  {
    icon: Key,
    title: 'Cryptographic Audit Trail',
    titleFil: 'Cryptographic Audit Trail',
    description: 'Secure SHA-256 hashing for immutable verification records and enterprise compliance.',
    descriptionFil: 'Secure na SHA-256 hashing para sa immutable verification records.'
  }
];

// Philippine IDs supported
const supportedIds = [
  { name: 'PhilID', full: 'Philippine National ID', agency: 'PSA', status: 'QR + Template Match' },
  { name: 'Passport', full: 'Philippine Passport', agency: 'DFA', status: 'MRZ + Biometric' },
  { name: "Driver's License", full: 'LTO License', agency: 'LTO', status: 'PDF417 Scan' },
  { name: 'UMID', full: 'Unified Multi-Purpose ID', agency: 'SSS/GSIS', status: 'OCR Assessment' },
  { name: 'Postal ID', full: 'Philippine Postal ID', agency: 'PHLPost', status: 'QR Scan' },
  { name: 'PRC ID', full: 'Professional License', agency: 'PRC', status: 'OCR Assessment' },

  { name: 'TIN Card', full: 'Tax Identification Number', agency: 'BIR', status: 'OCR Assessment' },
  { name: 'PhilHealth', full: 'PhilHealth ID', agency: 'PhilHealth', status: 'OCR Assessment' },
  { name: "Voter's ID", full: 'COMELEC ID', agency: 'COMELEC', status: 'OCR Assessment' },
];

// Payment methods
const paymentMethods = [
  { name: 'Bank QR / InstaPay', users: 'All PH banks' },
  { name: 'GCash', users: 'Coming soon' },
  { name: 'Maya', users: 'Coming soon' },
];

// Use cases for Philippine market
const useCases = [
  {
    icon: Building2,
    title: 'Digital Banks',
    titleFil: 'Digital Banks',
    description: 'Engineered for BSP eKYC compliance – pilot-ready for digital banks and fintechs',
    clients: ['Pilot Ready', 'BSP Sandbox']
  },
  {
    icon: CreditCard,
    title: 'Lending Apps',
    titleFil: 'Lending Apps',
    description: 'AMLA-compliant borrower verification for online lending',
    clients: ['Tala', 'Cashalo', 'JuanHand']
  },
  {
    icon: Smartphone,
    title: 'E-Commerce',
    titleFil: 'E-Commerce',
    description: 'Seller verification to reduce COD fraud',
    clients: ['Lazada', 'Shopee', 'Zalora']
  },
  {
    icon: BadgeCheck,
    title: 'HR & Recruitment',
    titleFil: 'HR & Recruitment',
    description: 'Employee background verification',
    clients: ['JobStreet', 'Kalibrr', 'OnlineJobs.ph']
  }
];

// Credit bundle pricing — 4-tier model for clean UX
const creditBundles = [
  {
    name: 'Personal',
    price: '100',
    basicCredits: 4,
    enhancedCredits: 2,
    bonus: '0%',
    effectivePrice: '₱25',
    forIndividuals: true,
  },
  {
    name: 'Starter',
    price: '500',
    basicCredits: 20,
    enhancedCredits: 11,
    bonus: '0%',
    effectivePrice: '₱25',
  },
  {
    name: 'Business',
    price: '2,500',
    basicCredits: 120,
    enhancedCredits: 66,
    bonus: '+20%',
    effectivePrice: '₱21',
    featured: true,
  },
  {
    name: 'Enterprise',
    price: '5,000',
    basicCredits: 260,
    enhancedCredits: 143,
    bonus: '+30%',
    effectivePrice: '₱19',
  },
];

// Compliance badges
const complianceBadges = [
  { name: 'BSP eKYC Compliance-Ready', description: 'Bangko Sentral ng Pilipinas' },
  { name: 'Registration in Progress', description: 'National Privacy Commission' },
  { name: 'AMLA Compliant', description: 'Anti-Money Laundering Act' },
  { name: 'Security-First Architecture', description: '24-hr Purge • Encrypted • Audit Trails' },
];

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } }
};

export default function LandingPage() {
  const [isDemoModalOpen, setIsDemoModalOpen] = useState(false);
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [language, setLanguage] = useState<'en' | 'fil'>('en');

  const getText = (en: string, fil: string) => language === 'en' ? en : fil;

  return (
    <div className="relative w-full overflow-hidden">
      {/* Animated Particle Network Background */}
      <ParticleNetwork
        particleCount={70}
        particleColor="59, 130, 246"
        lineColor="59, 130, 246"
        maxDistance={150}
        speed={0.3}
        mouseRadius={180}
        mouseForce={0.015}
      />

      {/* Floating Geometric Shapes */}
      <FloatingShapes />

      {/* Vignette Overlay for better readability */}
      <div
        className="fixed inset-0 pointer-events-none z-[1]"
        style={{
          background: 'radial-gradient(ellipse at center, transparent 0%, rgba(15, 23, 42, 0.8) 100%)',
        }}
      />

      {/* Hero Section */}
      <section className="relative z-10 min-h-screen flex items-center justify-center px-4 pt-8 pb-12">
        <div className="max-w-5xl mx-auto text-center relative z-10">
          {/* Language Toggle */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex justify-center mb-6"
          >
            <div className="inline-flex bg-gray-800/50 rounded-full p-1 backdrop-blur-sm border border-gray-700/50">
              <button
                type="button"
                onClick={() => setLanguage('en')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-300 ${language === 'en'
                  ? 'bg-blue-600/80 text-white'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                English
              </button>
              <button
                type="button"
                onClick={() => setLanguage('fil')}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-all duration-300 ${language === 'fil'
                  ? 'bg-blue-600/80 text-white'
                  : 'text-gray-400 hover:text-white'
                  }`}
              >
                Filipino
              </button>
            </div>
          </motion.div>

          {/* NPC Compliance Badge */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.2 }}
            className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 px-4 py-2 rounded-full mb-8 backdrop-blur-sm"
          >
            <CheckCircle2 className="w-4 h-4" />
            <span className="text-sm font-medium">
              {getText('NPC Registration in Progress • DPA Ready', 'NPC Registration in Progress • DPA Ready')}
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.8 }}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight"
          >
            <span className="text-white">
              {getText('Affordable, secure ID verification', 'Abot-kayang, secure na ID verification')}
            </span>
            <br />
            <span className="bg-gradient-to-r from-blue-400 via-cyan-400 to-blue-500 bg-clip-text text-transparent">
              {getText('for Philippine fintech and digital platforms', 'para sa Philippine fintech at digital platforms')}
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
            className="text-lg md:text-xl text-gray-400 mb-2 max-w-3xl mx-auto leading-relaxed"
          >
            {getText(
              'Agos enables remote customer onboarding using Philippine IDs, biometrics, and local data handling — designed for startups and growing platforms that need reliable verification without enterprise vendor pricing.',
              'Nagbibigay-daan ang Agos para sa remote customer onboarding gamit ang Philippine IDs, biometrics, at local data handling — idinisenyo para sa mga startup at lumalagong platform na nangangailangan ng maaasahang verification nang walang enterprise vendor pricing.'
            )}
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="text-sm text-blue-400/80 mb-8 font-medium tracking-wide uppercase"
          >
            {getText(
              'Built with Philippine regulatory frameworks in mind, including BSP eKYC and AML guidelines and NPC data privacy principles.',
              'Binuo na isinasaalang-alang ang mga Philippine regulatory framework, kabilang ang BSP eKYC at AML guidelines at NPC data privacy principles.'
            )}
          </motion.p>

          {/* CTA Buttons */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="flex flex-col sm:flex-row gap-4 justify-center mb-12"
          >
            <motion.button
              type="button"
              whileHover={{ scale: 1.03, boxShadow: '0 0 30px rgba(59, 130, 246, 0.3)' }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setIsDemoModalOpen(true)}
              className="group px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-xl font-semibold transition-all duration-300 flex items-center justify-center gap-2"
            >
              {getText('Schedule Demo', 'Mag-schedule ng Demo')}
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </motion.button>
            <motion.button
              type="button"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => setIsContactModalOpen(true)}
              className="px-8 py-4 bg-gray-800/50 border border-gray-700 text-gray-300 rounded-xl font-semibold hover:bg-gray-800 hover:border-gray-600 transition-all duration-300 backdrop-blur-sm"
            >
              {getText('Contact Sales', 'Makipag-ugnay sa Sales')}
            </motion.button>
          </motion.div>

          {/* Pricing Preview with Comparison */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9 }}
            className="flex flex-wrap justify-center gap-3 mb-8"
          >
            <span className="text-gray-500 text-sm">
              {getText('Credit bundles from', 'Credit bundles mula')}
            </span>
            <span className="text-white font-bold text-sm">
              ₱100 (₱25/verification)
            </span>
            <span className="text-gray-500 text-sm">•</span>
            <span className="text-gray-400 text-sm">
              {getText('Pay via Bank QR / InstaPay', 'Magbayad via Bank QR / InstaPay')}
            </span>
            <span className="text-gray-500 text-sm">•</span>
            <span className="text-emerald-400 text-sm font-medium">
              {getText('vs ₱110-330 from international vendors', 'vs ₱110-330 mula sa international vendors')}
            </span>
          </motion.div>

          {/* Scroll Indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            className="absolute bottom-8 left-1/2 -translate-x-1/2"
          >
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ repeat: Infinity, duration: 2 }}
              className="text-gray-500"
            >
              <ChevronDown className="w-6 h-6" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Animated Gradient Background Wrapper for Features */}
      <div className="relative z-10 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 bg-[length:200%_200%] animate-gradient">
        {/* Supported Philippine IDs */}
      <section className="relative z-10 py-16 px-4 border-t border-gray-800/50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={fadeInUp}
            className="text-center mb-10"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {getText('Supported Government IDs', 'Mga Suportadong Government IDs')}
            </h2>
            <p className="text-gray-400">
              {getText('All major Philippine identification documents', 'Lahat ng major na Philippine identification documents')}
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="flex flex-wrap justify-center gap-3"
          >
            {supportedIds.map((id) => (
              <motion.div
                key={id.name}
                variants={scaleIn}
                whileHover={{ scale: 1.05, borderColor: 'rgba(59, 130, 246, 0.5)', backgroundColor: 'rgba(30, 41, 59, 0.9)' }}
                className="group bg-slate-800/80 border border-slate-700 rounded-xl px-6 py-5 backdrop-blur-md cursor-default transition-all duration-300 min-w-[200px] shadow-lg hover:shadow-blue-500/10"
              >
                <div className="flex flex-col gap-3">
                  <div className="flex justify-between items-start gap-4">
                    <span className="text-white font-extrabold text-lg tracking-tight">{id.name}</span>
                    <span className="shrink-0 text-[10px] text-blue-300 font-bold uppercase tracking-wider bg-blue-900/50 border border-blue-700/30 px-2 py-1 rounded-md">
                      {id.status}
                    </span>
                  </div>
                  <div className="text-slate-300 text-xs font-semibold uppercase tracking-wider flex items-center gap-2">
                    <div className="w-1 h-1 rounded-full bg-blue-400"></div>
                    {id.agency} Issued
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Core Features */}
      <section className="relative z-10 py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {getText('Enterprise-Grade Verification', 'Enterprise-Grade na Verification')}
            </h2>
            <p className="text-gray-400 max-w-2xl mx-auto">
              {getText(
                'Engineered for BSP eKYC Compliance. Built for NPC-ready architecture with localized data residency.',
                'Dinisenyo para sa BSP eKYC Compliance. Ginawa para sa NPC-ready architecture na may localized data residency.'
              )}
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {coreFeatures.map((feature, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                whileHover={{ y: -5, borderColor: 'rgba(59, 130, 246, 0.3)' }}
                className="group bg-gray-800/20 border border-gray-800 rounded-2xl p-6 backdrop-blur-sm transition-all duration-300"
              >
                <div className="w-12 h-12 bg-blue-500/10 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-500/20 transition-colors">
                  {feature.icon === 'logo' ? (
                  <img
                      src="/Agos_logo.png"
                      alt="Agos Identity Logo"
                      className="w-6 h-6 object-contain"
                    />
                  ) : (
                    <feature.icon className="w-6 h-6 text-blue-400" />
                  )}
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  {getText(feature.title, feature.titleFil)}
                </h3>
                <p className="text-gray-400 text-sm leading-relaxed">
                  {getText(feature.description, feature.descriptionFil)}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
      </div>

      {/* Use Cases */}
      <section className="relative z-10 py-20 px-4 bg-gray-900/50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {getText('Built for Philippine Industries', 'Ginawa para sa Philippine Industries')}
            </h2>
            <p className="text-gray-400">
              {getText('Engineered for leading digital banks and fintechs', 'Dinisenyo para sa mga nangungunang digital bank at fintech')}
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {useCases.map((useCase, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                whileHover={{ y: -5 }}
                className="bg-gradient-to-b from-gray-800/40 to-gray-800/20 border border-gray-700/50 rounded-2xl p-6 backdrop-blur-sm"
              >
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500/20 to-purple-500/20 rounded-lg flex items-center justify-center mb-4">
                  <useCase.icon className="w-5 h-5 text-blue-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  {getText(useCase.title, useCase.titleFil)}
                </h3>
                <p className="text-gray-400 text-sm mb-4">
                  {useCase.description}
                </p>
                <div className="flex flex-wrap gap-1">
                  {useCase.clients.map((client, i) => (
                    <span key={i} className="text-xs text-gray-500 bg-gray-800/50 px-2 py-1 rounded">
                      {client}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="relative z-10 py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={fadeInUp}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {getText('Credit Bundle Pricing', 'Credit Bundle Pricing')}
            </h2>
            <p className="text-gray-400">
              {getText('Buy verification credits in bundles. Save more with larger bundles.', 'Bumili ng verification credits sa bundles. Mas makatipid sa mas malalaking bundle.')}
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
          >
            {creditBundles.map((bundle) => (
              <motion.div
                key={bundle.name}
                variants={scaleIn}
                whileHover={{ scale: 1.02 }}
                className={`bg-gray-800/30 border rounded-xl p-5 backdrop-blur-sm transition-all duration-300 ${bundle.featured ? 'border-blue-500/50 ring-1 ring-blue-500/30' : 'border-gray-700/50'
                  }`}
              >
                {bundle.featured && (
                  <div className="text-xs text-blue-400 font-medium mb-2 text-center">
                    {getText('POPULAR', 'POPULAR')}
                  </div>
                )}
                <div className="text-center mb-3">
                  <div className="text-white font-semibold text-lg">{bundle.name}</div>
                  {bundle.bonus !== '0%' && (
                    <div className="text-emerald-400 text-xs font-medium">{bundle.bonus} bonus credits</div>
                  )}
                </div>
                <div className="text-center mb-4">
                  <span className="text-lg text-gray-400">₱</span>
                  <span className="text-3xl font-bold text-white">{bundle.price}</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between text-gray-400">
                    <span>{getText('Basic credits', 'Basic credits')}</span>
                    <span className="text-white font-medium">{bundle.basicCredits}</span>
                  </div>
                  <div className="flex justify-between text-gray-400">
                    <span>{getText('Enhanced credits', 'Enhanced credits')}</span>
                    <span className="text-white font-medium">{bundle.enhancedCredits}</span>
                  </div>
                  <div className="border-t border-gray-700/50 pt-2 mt-2">
                    <div className="flex justify-between text-gray-400">
                      <span>{getText('Effective price', 'Effective price')}</span>
                      <span className="text-emerald-400 font-medium">{bundle.effectivePrice}/ea</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Payment Methods */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="flex flex-wrap justify-center items-center gap-4 mb-8"
          >
            <span className="text-gray-500 text-sm">
              {getText('Pay with:', 'Magbayad gamit ang:')}
            </span>
            {paymentMethods.map((method) => (
              <div key={method.name} className="flex items-center gap-1 text-gray-400 text-sm">
                <span>{method.name}</span>
                {method.users && <span className="text-gray-600 text-xs">({method.users})</span>}
              </div>
            ))}
          </motion.div>

          {/* Pricing Comparison Table */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-8 overflow-x-auto"
          >
            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left py-3 px-4 text-gray-400 font-medium">{getText('Feature', 'Feature')}</th>
                  <th className="text-center py-3 px-4 text-blue-400 font-semibold">Agos Identity</th>
                  <th className="text-center py-3 px-4 text-gray-500 font-medium">{getText('International Vendors', 'International Vendors')}</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-gray-800">
                  <td className="py-3 px-4 text-gray-300">{getText('Price per verification', 'Presyo bawat verification')}</td>
                  <td className="py-3 px-4 text-center text-emerald-400 font-bold">{getText('₱19-25 (bundles)', '₱19-25 (bundles)')}</td>
                  <td className="py-3 px-4 text-center text-gray-500">₱110 - ₱330</td>
                </tr>
                <tr className="border-b border-gray-800">
                  <td className="py-3 px-4 text-gray-300">
                    <div className="font-medium text-white">{getText('PhilSys Integration', 'PhilSys Integration')}</div>
                    <div className="text-[10px] text-gray-500 mt-1">{getText('via BSP Sandbox Pathway (Q2 2026)', 'via BSP Sandbox Pathway (Q2 2026)')}</div>
                  </td>
                  <td className="py-3 px-4 text-center text-emerald-400">
                    <div className="font-bold text-xs uppercase tracking-wider">{getText('Template Matching', 'Template Matching')}</div>
                    <div className="text-[9px] text-gray-500 mt-0.5">{getText('Visual Verification', 'Visual Verification')}</div>
                  </td>
                  <td className="py-3 px-4 text-center text-gray-500">✗ {getText('Not available', 'Hindi available')}</td>
                </tr>
                <tr className="border-b border-gray-800">
                  <td className="py-3 px-4 text-gray-300">{getText('Local Payment (Bank QR/InstaPay)', 'Local Payment (Bank QR/InstaPay)')}</td>
                  <td className="py-3 px-4 text-center text-emerald-400">✓</td>
                  <td className="py-3 px-4 text-center text-gray-500">✗ {getText('Credit card only', 'Credit card lang')}</td>
                </tr>
                <tr className="border-b border-gray-800">
                  <td className="py-3 px-4 text-gray-300">{getText('BSP eKYC Ready', 'BSP eKYC Ready')}</td>
                  <td className="py-3 px-4 text-center text-emerald-400">✓</td>
                  <td className="py-3 px-4 text-center text-gray-500">~ {getText('Manual compliance', 'Manual compliance')}</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 text-gray-300">{getText('Philippine ID Templates', 'Philippine ID Templates')}</td>
                  <td className="py-3 px-4 text-center text-emerald-400">11</td>
                  <td className="py-3 px-4 text-center text-gray-500">3-5</td>
                </tr>
              </tbody>
            </table>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 font-medium transition-colors"
            >
              {getText('View Full Pricing', 'Tingnan ang Buong Pricing')}
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Compliance Section */}
      <section className="relative z-10 py-20 px-4 bg-gray-900/50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={fadeInUp}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {getText('Compliance-First', 'Compliance-First')}
            </h2>
            <p className="text-gray-400">
              {getText('Engineered for Philippine regulatory standards', 'Dinisenyo para sa mga Philippine regulatory standards')}
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-2 md:grid-cols-4 gap-4"
          >
            {complianceBadges.map((badge) => (
              <motion.div
                key={badge.name}
                variants={fadeInUp}
                className="bg-gray-800/30 border border-gray-700/50 rounded-xl p-4 text-center backdrop-blur-sm"
              >
                <CheckCircle2 className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                <div className="text-white font-medium text-sm">{badge.name}</div>
                <div className="text-gray-500 text-xs">{badge.description}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="relative z-10 py-24 px-4">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={scaleIn}
            className="bg-gradient-to-b from-gray-800/50 to-gray-900/50 border border-gray-700/50 rounded-3xl p-8 md:p-12 text-center backdrop-blur-sm"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              {getText('Ready to Start?', 'Handa Ka Na Ba?')}
            </h2>
            <p className="text-gray-400 mb-8 max-w-lg mx-auto">
              {getText(
                'Join Philippine banks and fintechs using Agos Identity for secure, compliant identity verification.',
                'Sumali sa mga Philippine banks at fintechs na gumagamit ng Agos Identity para sa secure at compliant na identity verification.'
              )}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                type="button"
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setIsDemoModalOpen(true)}
                className="px-8 py-4 bg-blue-600 text-white rounded-xl font-semibold hover:bg-blue-500 transition-colors"
              >
                {getText('Schedule Demo', 'Mag-schedule ng Demo')}
              </motion.button>
              <Link
                to="/register"
                className="px-8 py-4 bg-gray-800 border border-gray-700 text-gray-300 rounded-xl font-semibold hover:bg-gray-700 transition-colors text-center"
              >
                {getText('Create Free Account', 'Gumawa ng Libreng Account')}
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Modals */}
      <BookDemoModal
        isOpen={isDemoModalOpen}
        onClose={() => setIsDemoModalOpen(false)}
      />
      <ContactSalesModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
      />
    </div>
  );
}
